import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setOnUnauthorized, setOnPaymentRequired } from "./api";

const AuthCtx = createContext(null);

const LAST_ACTIVE_KEY = "cc-last-active";
const LOCKED_KEY = "cc-locked";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=authed
  const [trialOver, setTrialOver] = useState(false);
  const [locked, setLocked] = useState(() => localStorage.getItem(LOCKED_KEY) === "1");

  const applyUser = useCallback((data) => {
    setUser(data);
    if (data.subscription_status === "trial") {
      const end = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
      setTrialOver(end ? end < new Date() : false);
    } else {
      setTrialOver(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
      return data;
    } catch {
      // Access token may have expired while the app was closed — try a
      // silent refresh-token renewal before giving up.
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        applyUser(data);
        return data;
      } catch {
        setUser(false);
        return null;
      }
    }
  }, [applyUser]);

  useEffect(() => {
    refresh();
    setOnUnauthorized(() => () => setUser(false));
    setOnPaymentRequired(() => () => {
      // Mark trial as over so the app shows the paywall UI
      setTrialOver(true);
      if (typeof window !== "undefined") {
        const p = window.location.pathname;
        if (!p.startsWith("/billing") && !p.startsWith("/signin") && !p.startsWith("/signup") && !p.startsWith("/settings")) {
          window.location.replace("/billing?expired=1");
        }
      }
    });
  }, [refresh]);

  // --- PIN lock: track inactivity, lock after the user-chosen interval ----
  useEffect(() => {
    if (!user || user === false) return undefined;
    const mins = user.pin_lock_minutes || 0;
    if (!user.pin_enabled || !mins) {
      setLocked(false);
      localStorage.removeItem(LOCKED_KEY);
      return undefined;
    }
    const mark = () => {
      if (localStorage.getItem(LOCKED_KEY) !== "1") {
        localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
      }
    };
    const check = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || Date.now());
      if (Date.now() - last > mins * 60000) {
        localStorage.setItem(LOCKED_KEY, "1");
        setLocked(true);
      }
    };
    mark();
    check();
    window.addEventListener("pointerdown", mark);
    window.addEventListener("keydown", mark);
    const iv = setInterval(check, 15000);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  const unlock = useCallback(() => {
    localStorage.removeItem(LOCKED_KEY);
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
    setLocked(false);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    unlock();
    applyUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    unlock();
    applyUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(LOCKED_KEY);
    setUser(false);
    window.location.href = "/signin";
  };

  return (
    <AuthCtx.Provider value={{ user, trialOver, locked, unlock, refresh, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
